import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, from, map, of } from 'rxjs';
import { AuthSession, Profile } from '../models/profile';
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';

const AUTHLOCALSTORAGETOKEN = `azUser`;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentUser$: Observable<any>;
  currentUserSubject: BehaviorSubject<any>;
  profileSubject: BehaviorSubject<any>;

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  constructor() {
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.profileSubject = new BehaviorSubject<any>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  loginSSO(provider: string): Observable<any> {
    return this.amplifyLoginSSO(provider).pipe(
      map((loginResult: any) => {
        console.log('Login result: ', JSON.stringify(loginResult));
        return loginResult;
      }),
      catchError((err) => {
        console.log('Error logging in', err);
        throw new Error(err.message);
      })
    );
  }

  logout() {
    this.amplifyLogout();
    localStorage.removeItem(AUTHLOCALSTORAGETOKEN);
    this.currentUserSubject.next(null);
    this.profileSubject.next(undefined);
  }

  getUserBySession(): Observable<AuthSession | undefined> {
    return this.amplifyGetUserSession().pipe(
      map((user: any) => {
        console.log('this is user by session: ${JSON.stringify(user)}`);');
        if (user) {
          const result = this.getUserAuthDataMap(user);
          this.setAuthFromLocalStorage(result);
          this.currentUserSubject.next(result);
          return result;
        } else {
          this.logout();
        }
      }),
      catchError((err) => {
        return of(undefined);
      })
    );
  }

  getAuthFromLocalStorage(): AuthSession | undefined {
    try {
      const lsValue = localStorage.getItem(AUTHLOCALSTORAGETOKEN);
      if (!lsValue) {
        return undefined;
      }
      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      return undefined;
    }
  }

  setAuthFromLocalStorage(auth: AuthSession | undefined): boolean {
    if (auth && auth.session) {
      localStorage.setItem(AUTHLOCALSTORAGETOKEN, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getUserAuthDataMap(loginResult: any): AuthSession {
    const result: AuthSession = {
      userId: loginResult.userSub,
      session: loginResult.tokens,
      payload: {
        sub: loginResult.tokens.idToken.payload.sub,
        firstname: loginResult.tokens.idToken.payload.given_name,
        lastname: loginResult.tokens.idToken.payload.middle_name,
        email: loginResult.tokens.idToken.payload.email,
        email_verified: loginResult.tokens.idToken.payload.email_verified,
        groups: loginResult.tokens.idToken.payload['cognito:groups']
          ? loginResult.tokens.idToken.payload['cognito:groups']
          : '',
        adgroups: loginResult.tokens.idToken.payload['custom:adgroups']
          ? loginResult.tokens.idToken.payload['custom:adgroups']
          : '',
        exp: loginResult.tokens.idToken.payload.exp
          ? loginResult.tokens.idToken.payload.exp
          : 0,
      },
    };
    return result;
  }

  getProfile() {
    if (!this.currentUserValue) {
      return false;
    }

    const payload = this.currentUserValue.session.idToken.payload;
    const { email, middle_name, given_name } = payload;
    // custom:adgroups is absent when the IdP attribute mapping doesn't supply it.
    const rawGroups: string = (payload['custom:adgroups'] as string | undefined) ?? '';
    const groups = (
      rawGroups.startsWith('[')
        ? rawGroups.substring(1, rawGroups.length - 1).split(', ')
        : rawGroups.split(' ')
    ).filter(Boolean);
    const prid = payload['custom:prid'];

    const profile: Profile = {
      prid: prid,
      email: email,
      userPrincipalName: given_name,
      familyName: middle_name,
      givenName: given_name,
      groups,
      lastUpdate: new Date().toDateString(),
    };

    this.profileSubject.next(profile);

    return profile;
  }

  // http auth service
  amplifyLoginSSO(provider: string): Observable<any> {
    try {
      const authResult = signInWithRedirect({
        provider: {
          custom: provider,
        },
      });
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGIN-ERROR: ' + error);
    }
  }

  /**
   * Logs the user in with the username and password
   * @param email
   * @param password
   */
  amplifyLogout(): Observable<any> {
    try {
      const logoutResult = signOut();
      return from(logoutResult);
    } catch (error) {
      throw new Error('AZ-AUTH-LOGOUT-ERROR: ' + error);
    }
  }

  amplifyGetUserSession(): Observable<any> {
    try {
      const authResult = fetchAuthSession();
      return from(authResult);
    } catch (error) {
      throw new Error('AZ-AUTH-GETUSER-ERROR: ' + error);
    }
  }
}
